import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { candidatesAPI, jobsAPI, profileScreeningsAPI, resumesAPI } from '../api/client';
import { toast } from 'react-toastify';
import { CANDIDATE_STATUSES, getStatusStyle, getStatusLabel, normalizeStatus } from '../constants/candidateStatuses';
import { DEPARTMENT_OPTIONS } from '../constants/departmentOptions';
import LoadingButton from "../components/LoadingButton";
import ConfirmModal from "../components/ConfirmModal";
import { hasPermission } from "../utils/accessControl";
import { buildCandidateUpdatePayload } from "../utils/candidateUpdatePayload";
import { mayPerform, mayPerformWithRole } from "../utils/userAccess";
import '../pages/AnimationsAndStyles.css';

const parseDate = (str) => {
  if (!str) return null;
  const s = String(str);
  return new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
};

const SOURCE_OPTIONS = ['Naukri', 'LinkedIn', 'Employee Reference', 'Candidate Reference'];

/**
 * Normalises a legacy remark row into the timeline event shape.
 *
 * The merged timeline endpoint is the primary source for the "Remarks &
 * Timeline" section. This mapper is the fallback used when that endpoint is
 * unavailable, so the legacy `GET /candidates/:id/remarks` payload still
 * renders during a rolling deploy.
 */
export function toTimelineEvents(remarks = []) {
  return remarks.map((remark) => {
    let changedFields = [];
    try {
      const raw = remark.changed_fields;
      if (raw) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed)) changedFields = parsed;
      }
    } catch (e) {
      changedFields = [];
    }

    return {
      key: `remark:${remark.id}`,
      type: remark.remark_type,
      title: remark.title,
      description: remark.description || remark.remark_text || null,
      actor: remark.updated_by_name || remark.created_by_name || 'System',
      occurredAt: remark.created_at,
      status: remark.status || null,
      changedFields,
    };
  });
}

export function formatTimelineDate(value) {
  const parsed = parseDate(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return 'Date unavailable';
  return parsed.toLocaleString('en-IN', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
}

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [candidate, setCandidate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineNotice, setTimelineNotice] = useState(null);
  const [newRemark, setNewRemark] = useState('');
  const [showRemarkForm, setShowRemarkForm] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addingRemark, setAddingRemark] = useState(false);
  const [sendingScreening, setSendingScreening] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeDownloading, setResumeDownloading] = useState(false);

  /* ================= FETCH ================= */

  const fetchCandidate = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await candidatesAPI.getById(id);
      setCandidate(res.data);
      setFormData(res.data);
    } catch (err) {
      console.error('Failed to fetch candidate:', err);
      toast.error('Failed to fetch candidate');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidate();
    fetchTimeline();
    fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!editMode && document.visibilityState === "visible") {
        fetchCandidate(true);
        fetchTimeline();
        fetchResumes();
      }
    }, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editMode]);

  const fetchTimeline = async () => {
    try {
      const res = await candidatesAPI.getTimeline(id);
      setTimeline(res.data?.events || []);
      setTimelineNotice(res.data?.unexplainedStatus || null);
    } catch (err) {
      console.error('Failed to fetch candidate timeline:', err);
      // Fall back to the legacy remarks endpoint if the new timeline is
      // unavailable (e.g. during a rolling deploy).
      try {
        const res = await candidatesAPI.getRemarks(id);
        setRemarks(res.data || []);
        setTimeline([]);
        setTimelineNotice(null);
      } catch (fallbackErr) {
        console.error('Failed to fetch remarks:', fallbackErr);
      }
    }
  };

  const fetchResumes = async () => {
    try {
      setResumeLoading(true);
      const res = await resumesAPI.getByCandidate(id);
      const list = res.data || [];
      setResumes(list);
    } catch (err) {
      console.error('Failed to fetch resumes:', err);
    } finally {
      setResumeLoading(false);
    }
  };

  // Extract a friendly file name from the S3 resume path
  const getResumeFileName = (path) => {
    if (!path) return 'Resume';
    try {
      return decodeURIComponent(path.split('/').pop().split('?')[0]) || 'Resume';
    } catch {
      return 'Resume';
    }
  };

  const handleResumeDownload = async (resumeId) => {
    if (resumeDownloading) return;
    setResumeDownloading(true);
    try {
      const res = await resumesAPI.download(resumeId);
      const url = res.data?.downloadUrl;
      if (!url) {
        toast.error('No download URL received');
        return;
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Resume download failed:', err);
      toast.error('Download failed');
    } finally {
      setResumeDownloading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await jobsAPI.getAll();
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  };

  const canSendScreening = candidate && normalizeStatus(candidate.status) === "screen_pending";
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const canEditCandidate = mayPerformWithRole(user?.role, "candidate", "edit", "candidate.edit");

  const sendForScreening = async () => {
    if (sendingScreening) return;
    if (!canSendScreening) return;
    setSendingScreening(true);
    try {
      await profileScreeningsAPI.send(id);
      toast.success("Profile sent for screening");
      await Promise.all([fetchCandidate(), fetchTimeline()]);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "HIRING_MANAGER_REQUIRED") {
        setShowManagerModal(true);
      } else if (code === "JOB_REQUIRED") {
        setShowJobModal(true);
      } else if (code === "SCREENING_PENDING") {
        toast.info("A screening request is already pending for this candidate.");
      } else {
        toast.error(err.response?.data?.error || "Failed to send profile for screening");
      }
    } finally {
      setSendingScreening(false);
    }
  };

  useEffect(() => {
    if (location.state?.autoSendScreening && candidate && canSendScreening) {
      navigate(location.pathname, { replace: true, state: {} });
      sendForScreening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.autoSendScreening, candidate?.id]);

  /* ================= CHANGE ================= */

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ================= SAVE ================= */

  const TRACKED_FIELD_LABELS = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email_id: 'Email',
    phone_number: 'Phone',
    alternate_mobile_number: 'Alt. Phone',
    position: 'Position',
    department: 'Department',
    status: 'Status',
    custom_current_employer: 'Current Employer',
    custom_overall_experience_years: 'Total Experience',
    custom_relevant_experience_years: 'Relevant Experience',
    notice_period: 'Notice Period',
    joining_date: 'Joining Date',
    custom_current_salary_lpa: 'Current Salary',
    custom_expected_salary_lpa: 'Expected Salary',
    custom_current_location: 'Current Location',
    custom_preferred_location: 'Preferred Location',
    offer_in_hand: 'Offer in Hand',
    variable_pay_details: 'Variable Pay',
    rsu_details: 'RSU Details',
    source: 'Source',
    skills: 'Skills',
    education: 'Education',
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Detect changed fields
      const changedFields = [];
      Object.entries(TRACKED_FIELD_LABELS).forEach(([key, label]) => {
        const oldVal = String(candidate[key] ?? '').trim();
        const newVal = String(formData[key] ?? '').trim();
        if (oldVal !== newVal) {
          changedFields.push({ field: label, from: oldVal || '—', to: newVal || '—' });
        }
      });

      // Detect status change for remark type
      const remarkType = changedFields.some(f => f.field === 'Status')
        ? 'status_change'
        : 'profile_update';

      await candidatesAPI.update(
        id,
        buildCandidateUpdatePayload(formData, { candidate, note })
      );

      // Always log a remark when there are changes or a manual note
      if (changedFields.length > 0 || note) {
        await candidatesAPI.addRemark(id, {
          remark_type: remarkType,
          title: 'Profile Updated',
          description: note || null,
          changed_fields: changedFields,
        });
        fetchTimeline();
      }

      toast.success('Candidate updated successfully');
      setEditMode(false);
      setNote('');
      fetchCandidate();
    } catch (err) {
      console.error('Update failed:', err);
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) {
      toast.error('Please enter a remark');
      return;
    }
    if (addingRemark) return;
    setAddingRemark(true);

    try {
      await candidatesAPI.addRemark(id, {
        remark_type: 'custom',
        title: 'HR Note',
        description: newRemark
      });

      toast.success('Remark added successfully');
      setNewRemark('');
      setShowRemarkForm(false);
      fetchTimeline();
    } catch (err) {
      console.error('Add remark failed:', err);
      toast.error('Failed to add remark');
    } finally {
      setAddingRemark(false);
    }
  };

  /* ================= LOADING STATE ================= */

    if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="candidate-detail-page">
          <div className="candidate-card loading-card">
            <div className="loading-spinner"></div>
            <h3>Loading candidate details...</h3>
          </div>
        </div>
      </>
    );
  }

  if (!candidate) {
    return (
      <>
        <style>{css}</style>
        <div className="candidate-detail-page">
          <div className="candidate-card">
            <p>Candidate not found</p>
            <button className="btn-back" onClick={() => navigate('/candidates')}>Back to List</button>
          </div>
        </div>
      </>
    );
  }

  // The merged timeline is the primary source. `remarks` is kept as a
  // fallback so the section still renders if the timeline endpoint is
  // unavailable.
  const timelineEvents = timeline.length ? timeline : toTimelineEvents(remarks);

  return (
    <>
      {/* ================= INLINE CSS ================= */}
      <style>{css}</style>

      <div className="candidate-detail-page">
        <div className="candidate-card">

          {/* ================= HEADER ================= */}
          <div className="card-header">
            <div className="header-left">
              <div className="avatar-large">
                {(candidate.first_name || 'C')[0].toUpperCase()}
              </div>
              <div className="header-info">
                {editMode ? (
                  <div className="name-edit-group">
                    <input
                      name="first_name"
                      value={formData.first_name || ''}
                      onChange={handleChange}
                      placeholder="First Name"
                      className="name-input"
                    />
                    <input
                      name="last_name"
                      value={formData.last_name || ''}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="name-input"
                    />
                  </div>
                ) : (
                  <>
                    <h1 className="candidate-name">
                      {candidate.first_name} {candidate.last_name}
                    </h1>
                    <p className="candidate-id">ID: #{candidate.id}</p>
                  </>
                )}
              </div>
            </div>

            <div className="header-actions">
              <button className="btn-back" onClick={() => navigate('/candidates')}>
                Back
              </button>

              {!editMode ? (
                <>
                  <LoadingButton
                    className="btn-save"
                    onClick={sendForScreening}
                    loading={sendingScreening}
                    loadingText="Sending..."
                    disabled={!canSendScreening}
                    title={!canSendScreening ? `Screening not available — current status: ${candidate?.status}` : "Send for Screening"}
                  >
                    {canSendScreening ? "Send for Screening" : normalizeStatus(candidate?.status) === "screen_pending" ? "Screening Pending" : "Screening Unavailable"}
                  </LoadingButton>
                  {canEditCandidate && (
                    <button className="btn-edit" onClick={() => setEditMode(true)}>
                      Edit
                    </button>
                  )}
                </>
              ) : (
                <>
                  <LoadingButton className="btn-save" onClick={handleSave} loading={saving} loadingText="Saving...">
                    Save
                  </LoadingButton>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setEditMode(false);
                      setFormData(candidate);
                      setNote('');
                    }}
                    >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ================= META INFO ================= */}
          <div className="meta-info-section">
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Created By</span>
                <span className="meta-value">{candidate.created_by_name || '—'}</span>
              </div>
            </div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Modified By</span>
                <span className="meta-value">{candidate.updated_by_name || '—'}</span>
              </div>
            </div>
            <div className="meta-info-section">
              <div className="meta-item">
                <span className="meta-icon"></span>
                <div>
                  <span className="meta-label">Uploaded On</span>
                  <span className="meta-value">
                    {candidate.created_at
                      ? parseDate(candidate.created_at).toLocaleString('en-IN', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            <div className="meta-divider"></div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Last Updated</span>
                <span className="meta-value">
                  {candidate.updated_at
                    ? parseDate(candidate.updated_at).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* ================= CONTACT INFORMATION ================= */}
          <div className="section">
            <h3 className="section-title">Contact Information</h3>
            <div className="section-grid">
              {renderField('Email Address', 'email_id', false)}
              {renderField('Phone Number', 'phone_number', false)}
              {renderField('Alternate Mobile Number', 'alternate_mobile_number', false)}
              {renderSourceField()}
              {renderField('Current Location', 'custom_current_location', false)}
              {renderField('Preferred Location', 'custom_preferred_location', false)}
            </div>
          </div>

          {/* ================= PROFESSIONAL DETAILS ================= */}
          <div className="section">
            <h3 className="section-title">Professional Details</h3>
            <div className="section-grid">
              {renderDepartmentField()}
              {renderPositionField()}
              {renderStatusField()}
              {renderField('Current Employer', 'custom_current_employer', false, '')}
              {renderField('Total Experience', 'custom_overall_experience_years', false, '', '')}
              {renderField('Relevant Experience', 'custom_relevant_experience_years', false, '', '')}
              {renderNoticePeriodField()}
              {renderJoiningDateField()}
            </div>
          </div>

          {/* ================= COMPENSATION ================= */}
          <div className="section">
            <h3 className="section-title">Compensation</h3>
            <div className="section-grid">
              {renderField('Current Salary', 'custom_current_salary_lpa', false, '', ' LPA')}
              {renderField('Expected Salary', 'custom_expected_salary_lpa', false, '', ' LPA')}
              {renderField('Offer in Hand', 'offer_in_hand', false, '', ' LPA')}
              {renderField('Variable Pay', 'variable_pay_details', false, '', ' LPA')}
              {renderField('RSU Details', 'rsu_details', false, '', '')}
            </div>
          </div>

          {/* ================= SKILLS & EDUCATION ================= */}
          <div className="section">
            <h3 className="section-title">Skills & Education</h3>
            <div className="section-grid-full">
              {renderField('Skills', 'skills', true, '')}
              {renderField('Education', 'education', true, '')}
            </div>
          </div>

          {/* ================= HR NOTE ================= */}
          {editMode && (
            <div className="note-section">
              <h3 className="section-title">HR Note</h3>
              <textarea
                className="note-textarea"
                rows={4}
                placeholder="Add a note about this update (optional)..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {/* ================= RESUME ================= */}
          {/* CSP note: frame-src blocks embedding the S3 signed URL in an iframe,
              so the resume is opened in a new browser tab (PDF viewer) instead. */}
          <div className="section">
            <h3 className="section-title" style={{ marginBottom: '16px' }}>Resume</h3>

            {resumeLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748B' }}>
                Loading resume...
              </div>
            ) : resumes.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748B' }}>
                No resume uploaded for this candidate.
              </div>
            ) : (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: '#F8FAFC',
                  }}
                >
                  <span style={{ fontSize: '24px' }}>📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#0F172A',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getResumeFileName(resumes[0].resume_file_path)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                      Uploaded by {resumes[0].updated_by_name || 'Unknown'}
                      {resumes[0].created_at
                        ? ` · ${parseDate(resumes[0].created_at).toLocaleDateString('en-IN')}`
                        : ''}
                      {resumes.length > 1 ? ` · ${resumes.length} versions available` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-edit"
                    onClick={() => handleResumeDownload(resumes[0].id)}
                    disabled={resumeDownloading}
                    style={{ padding: '6px 14px', fontSize: '13px' }}
                  >
                    {resumeDownloading ? 'Opening...' : '👁 Open Resume'}
                  </button>
                </div>

                {resumes.length > 1 && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {resumes.map((rv, i) => (
                      <button
                        key={rv.id}
                        type="button"
                        className="btn-edit"
                        onClick={() => handleResumeDownload(rv.id)}
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                      >
                        Version {resumes.length - i}
                        {rv.created_at ? ` · ${parseDate(rv.created_at).toLocaleDateString('en-IN')}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ================= REMARKS TIMELINE ================= */}
          <div className="section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="section-title">Remarks & Timeline</h3>
              {!showRemarkForm && !editMode && canEditCandidate && (
                <button
                  className="btn outline"
                  onClick={() => setShowRemarkForm(true)}
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Add Remark
                </button>
              )}
            </div>

            {/* Add Remark Form */}
            {showRemarkForm && !editMode && (
              <div style={{ 
                background: '#F0F9FF', 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #CBD5E1'
              }}>
                <textarea
                  value={newRemark}
                  onChange={(e) => setNewRemark(e.target.value)}
                  placeholder="Add your remark here..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #CBD5E1',
                    fontFamily: "var(--font-sans)",
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <LoadingButton
                    className="btn primary"
                    onClick={handleAddRemark}
                    loading={addingRemark}
                    loadingText="Saving..."
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Save Remark
                  </LoadingButton>
                  <button
                    className="btn outline"
                    onClick={() => setShowRemarkForm(false)}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Data-gap notice: the candidate row is on a stage that no
                status-history entry explains. Nothing is invented here - the
                notice points out the missing record. */}
            {timelineNotice && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                color: '#92400E',
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '12.5px'
              }}>
                <span style={{ fontWeight: '700' }}>Note:</span>
                <span>
                  The candidate is currently at <strong>{timelineNotice.currentStatusLabel || timelineNotice.currentStatus}</strong>,
                  {' '}but the last recorded stage change was <strong>{timelineNotice.lastLoggedStatusLabel || timelineNotice.lastLoggedStatus || 'not recorded'}</strong>
                  {timelineNotice.lastLoggedAt ? ` (${formatTimelineDate(timelineNotice.lastLoggedAt)})` : ''}.
                  {' '}No status-history entry explains this change.
                </span>
              </div>
            )}

            {/* Timeline */}
            <div style={{ position: 'relative' }}>
              {timelineEvents.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '32px', 
                  color: '#94A3B8',
                  fontSize: '14px'
                }}>
                  No activity recorded yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {timelineEvents.map((event, idx) => (
                    <div key={event.key || idx} style={{
                      display: 'flex',
                      gap: '16px',
                      position: 'relative',
                      paddingLeft: '32px'
                    }}>
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: getRemarkColor(event.type),
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px #E2E8F0',
                        zIndex: 2
                      }} />
                      
                      {/* Timeline line */}
                      {idx < timelineEvents.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          left: '6px',
                          top: '16px',
                          width: '2px',
                          height: 'calc(100% + 16px)',
                          background: '#E2E8F0',
                          zIndex: 1
                        }} />
                      )}

                      {/* Event content */}
                      <div style={{
                        flex: 1,
                        background: '#F8FAFC',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0'
                      }}>
                        {/* Header row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '4px'
                        }}>
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>
                            {getRemarkIcon(event.type)} {event.title}
                          </h4>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', color: '#0F172A', fontWeight: '500' }}>
                              {event.actor || 'System'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
                              {formatTimelineDate(event.occurredAt)}
                            </div>
                          </div>
                        </div>

                        {/* Status badge */}
                        {event.status && (
                          <div style={{ marginTop: '6px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: '#E0F2FE',
                              color: '#075985'
                            }}>
                              Status: {event.statusLabel || event.status}
                              {event.previousStatus ? ` (from ${event.previousStatusLabel || event.previousStatus})` : ''}
                            </span>
                          </div>
                        )}

                        {/* Manual note */}
                        {event.description && (
                          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#475569', fontStyle: 'italic' }}>
                            &ldquo;{event.description}&rdquo;
                          </p>
                        )}

                        {/* Changed fields diff */}
                        {event.changedFields && event.changedFields.length > 0 && (
                          <div style={{
                            marginTop: '10px',
                            borderTop: '1px solid #E2E8F0',
                            paddingTop: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            {event.changedFields.map((f, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontSize: '12px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '600', color: '#334155', minWidth: '110px' }}>{f.field}</span>
                                <span style={{ color: '#94A3B8', textDecoration: 'line-through' }}>{f.from}</span>
                                <span style={{ color: '#64748B' }}>→</span>
                                <span style={{ color: '#059669', fontWeight: '500' }}>{f.to}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showManagerModal}
        title="Hiring Manager Required"
        message="No Hiring Manager is assigned to this Job Opening. Add the manager on the Job Opening and screening will continue automatically."
        confirmLabel="Edit Job Opening"
        cancelLabel="Cancel"
        onCancel={() => setShowManagerModal(false)}
        onConfirm={() => navigate("/jobs", {
          state: { editJobId: candidate.job_id, screeningCandidateId: candidate.id }
        })}
      />
      <ConfirmModal
        isOpen={showJobModal}
        title="Job Opening Required"
        message="This candidate is not assigned to any Job Opening. Please assign a Job Opening before sending for screening."
        confirmLabel="Go to Candidates"
        cancelLabel="Cancel"
        onCancel={() => setShowJobModal(false)}
        onConfirm={() => navigate("/candidates")}
      />
    </>
  );

  /* ================= HELPERS ================= */

  function getRemarkColor(type) {
    const colors = {
      'status_change': '#3B82F6',
      'note': '#8B5CF6',
      'profile_update': '#10B981',
      'resume_upload': '#F59E0B',
      'custom': '#6366F1',
      'feedback': '#EC4899',
      'feedback_assessment': '#EC4899',
      'screening_sent': '#0EA5E9',
      'screening_opened': '#38BDF8',
      'screening_decision': '#8B5CF6',
      'interview_scheduled': '#14B8A6',
      'interview_rescheduled': '#2DD4BF',
      'interview_completed': '#22C55E',
      'interview_cancelled': '#EF4444',
      'interview_invited': '#06B6D4',
      'offer_created': '#6366F1',
      'offer_approved': '#4F46E5',
      'offer_released': '#4338CA',
      'offer_sent': '#7C3AED',
      'offer_accepted': '#16A34A',
      'offer_rejected': '#DC2626',
      'offer_workflow': '#A855F7',
    };
    return colors[type] || '#64748B';
  }

  function getRemarkIcon(type) {
    const icons = {
      'status_change': '[Status]',
      'profile_update': '[Edit]',
      'resume_upload': '[Resume]',
      'note': '[Note]',
      'custom': '[Note]',
      'feedback': '[Feedback]',
      'feedback_assessment': '[Feedback]',
      'screening_sent': '[Screening]',
      'screening_opened': '[Screening]',
      'screening_decision': '[Screening]',
      'interview_scheduled': '[Interview]',
      'interview_rescheduled': '[Interview]',
      'interview_completed': '[Interview]',
      'interview_cancelled': '[Interview]',
      'interview_invited': '[Interview]',
      'offer_created': '[Offer]',
      'offer_approved': '[Offer]',
      'offer_released': '[Offer]',
      'offer_sent': '[Offer]',
      'offer_accepted': '[Offer]',
      'offer_rejected': '[Offer]',
      'offer_workflow': '[Offer]',
    };
    return icons[type] || '';
  }

  function renderField(label, name, textarea = false, icon = '', suffix = '') {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          {label}
        </label>
        {editMode ? (
          textarea ? (
            <textarea
              name={name}
              value={formData[name] || ''}
              onChange={handleChange}
              rows={3}
              className="field-textarea"
            />
          ) : (
            <input
              name={name}
              value={formData[name] || ''}
              onChange={handleChange}
              className="field-input"
            />
          )
        ) : (
          <div className="field-value">
            {candidate[name] ? `${candidate[name]}${suffix}` : '—'}
          </div>
        )}
      </div>
    );
  }

  function renderDepartmentField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Department
        </label>
        {editMode ? (
          <select
            name="department"
            value={formData.department || ''}
            onChange={handleChange}
            className="field-select"
          >
            <option value="">Select Department</option>
            {DEPARTMENT_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <div className="field-value">
            {candidate.department || '—'}
          </div>
        )}
      </div>
    );
  }

  function renderSourceField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Source
        </label>
        {editMode ? (
          <select
            name="source"
            value={formData.source || ''}
            onChange={handleChange}
            className="field-select"
          >
            <option value="">Select Source</option>
            {SOURCE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <div className="field-value">
            {candidate.source || '—'}
          </div>
        )}
      </div>
    );
  }

  function renderPositionField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Position
        </label>
        {editMode ? (
          <select
            name="position"
            value={formData.position || ''}
            onChange={(e) => {
              const val = e.target.value;
              const selected = jobs.find(j => `${j.title} - ${j.client_name}` === val);
              setFormData(prev => ({
                ...prev,
                position: val,
                job_id: selected ? selected.id : null
              }));
            }}
            className="field-select"
          >
            <option value="">Select Position</option>
            {jobs.map(job => (
              <option
                key={job.id}
                value={`${job.title} - ${job.client_name}`}
              >
                {job.title} - {job.client_name}
              </option>
            ))}
          </select>
        ) : (
          <div className="field-value position-badge">
            {candidate.position || '—'}
          </div>
        )}
      </div>
    );
  }

  function renderStatusField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Status
        </label>
        {editMode ? (
          <select
            name="status"
            value={formData.status || 'applied'}
            onChange={handleChange}
            className="field-select"
          >
            {CANDIDATE_STATUSES.map(status => (
              <option key={status.key} value={status.key}>
                {status.label}
              </option>
            ))}
          </select>
        ) : (
          <span 
            className="status-badge"
            style={{
              ...getStatusStyle(candidate.status),
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {getStatusLabel(candidate.status)}
          </span>
        )}
      </div>
    );
  }

  function renderNoticePeriodField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Notice Period
        </label>
        {editMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <select
              name="notice_period"
              value={formData.notice_period || ''}
              onChange={handleChange}
              className="field-select"
            >
              <option value="">Select Notice Period</option>
              <option value="Immediate">Immediate</option>
              <option value="30 Days">30 Days</option>
              <option value="60 Days">60 Days</option>
              <option value="90 Days">90 Days</option>
              <option value="Serving Notice">Serving Notice</option>
            </select>
            
            {/* Show Last Working Day calendar only if "Serving Notice" is selected */}
            {formData.notice_period === 'Serving Notice' && (
              <div>
                <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                  Last Working Day
                </label>
                <input
                  type="date"
                  name="last_working_day"
                  value={formData.last_working_day || ''}
                  onChange={handleChange}
                  className="field-input"
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="field-value">
            <div>{formData.notice_period || '—'}</div>
            {formData.notice_period === 'Serving Notice' && formData.last_working_day && (
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>
                Last Working Day: {new Date(formData.last_working_day).toLocaleDateString('en-IN')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderJoiningDateField() {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          Joining Date
        </label>
        {editMode ? (
          <input
            type="date"
            name="joining_date"
            value={formData.joining_date || ''}
            onChange={handleChange}
            className="field-input"
          />
        ) : (
          <div className="field-value">
            {candidate.joining_date
              ? new Date(candidate.joining_date).toLocaleDateString('en-IN')
              : '—'}
          </div>
        )}
      </div>
    );
  }
}

/* ================= PROFESSIONAL INLINE CSS ================= */

const css = `
/* ================= PAGE LAYOUT ================= */

.candidate-detail-page {
  min-height: auto;
  background: transparent;
  padding: 4px 0;
  font-family: var(--font-sans);
  animation: fadeIn 0.5s ease-out;
}

.candidate-card {
  background: #FFFFFF;
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: slideUp 0.6s ease-out;
}
.loading-card {
  text-align: center;
  padding: 40px 24px;
}

.loading-spinner {
  font-size: 36px;
  margin-bottom: 12px;
  animation: spin 2s linear infinite;
}

/* ================= HEADER ================= */

.card-header {
  background: linear-gradient(135deg, #1E40AF, #1e3a8a);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  gap: 14px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #0EA5E9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
  color: white;
  box-shadow: var(--shadow-md);
  flex-shrink: 0;
}
.header-info {
  flex: 1;
}

.candidate-name {
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  font-family: var(--font-sans);
}

.candidate-id {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.name-edit-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.name-input {
  padding: 6px 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-family: var(--font-sans);
  min-width: 150px;
}

.name-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.name-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.6);
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ================= BUTTONS ================= */

button {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-back {
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-edit {
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-edit:hover {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.btn-save {
  background: linear-gradient(135deg, #10B981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-save:hover {
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.btn-cancel {
  background: linear-gradient(135deg, #EF4444, #DC2626);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-cancel:hover {
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}

/* ================= META INFO ================= */

.meta-info-section {
  display: flex;
  padding: 12px 24px;
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-icon {
  font-size: 18px;
}

.meta-label {
  display: block;
  font-size: 11px;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.meta-value {
  display: block;
  font-size: 13px;
  color: #0F172A;
  font-weight: 500;
  margin-top: 1px;
}

.meta-divider {
  width: 1px;
  height: 28px;
  background: #CBD5E1;
}

/* ================= SECTIONS ================= */

.section {
  padding: 24px;
  border-bottom: 1px solid #E2E8F0;
}

.section:last-child {
  border-bottom: none;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
  font-family: var(--font-sans);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}

.section-grid-full {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

/* ================= FIELDS ================= */

.detail-field {
  animation: fadeInUp 0.4s ease-out;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-family: var(--font-sans);
}

.field-icon {
  font-size: 14px;
}

.field-value {
  padding: 8px 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 14px;
  color: #0F172A;
  font-weight: 500;
  min-height: 36px;
  display: flex;
  align-items: center;
}

.field-input,
.field-select {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #CBD5E1;
  border-radius: 8px;
  font-size: 14px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.field-input:focus,
.field-select:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.field-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #CBD5E1;
  border-radius: 8px;
  font-size: 14px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  resize: vertical;
  box-sizing: border-box;
}

.field-textarea:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

/* ================= BADGES ================= */

.position-badge {
  background: linear-gradient(135deg, #EFF6FF, #DBEAFE);
  color: #1E40AF;
  font-weight: 600;
  border: 1px solid #BFDBFE;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  font-family: var(--font-sans);
}

.status-badge.status-applied {
  background: #EFF6FF;
  color: #1E40AF;
  border: 1px solid #BFDBFE;
}

.status-badge.status-shortlisted {
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #FCD34D;
}

.status-badge.status-interview {
  background: #DBEAFE;
  color: #1E3A8A;
  border: 1px solid #93C5FD;
}

.status-badge.status-offered {
  background: #D1FAE5;
  color: #065F46;
  border: 1px solid #6EE7B7;
}

.status-badge.status-rejected {
  background: #FEE2E2;
  color: #991B1B;
  border: 1px solid #FECACA;
}

/* ================= NOTE SECTION ================= */

.note-section {
  padding: 16px;
  background: #FFFBEB;
  border-top: 3px solid #FCD34D;
}

.note-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #FCD34D;
  border-radius: 8px;
  font-size: 13px;
  font-family: var(--font-sans);
  background: white;
  resize: vertical;
  box-sizing: border-box;
}

.note-textarea:focus {
  outline: none;
  border-color: #F59E0B;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

.note-textarea::placeholder {
  color: #94A3B8;
}

/* ================= ANIMATIONS ================= */

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ================= RESPONSIVE ================= */

@media (max-width: 768px) {
  .candidate-detail-page {
    padding: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions button {
    flex: 1;
    justify-content: center;
  }

  .candidate-name {
    font-size: 22px;
  }

  .avatar-large {
    width: 52px;
    height: 52px;
    font-size: 24px;
  }

  .section {
    padding: 16px;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }

  .meta-info-section {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
    gap: 12px;
  }

  .meta-divider {
    display: none;
  }

  .name-edit-group {
    flex-direction: column;
    width: 100%;
  }

  .name-input {
    width: 100%;
  }
}
`;

